// ─── Contentful Configuration ───
// Note: Read-only tokens in client-side code is standard practice per Contentful docs.
// The write (CMA) token is NOT here — it lives securely in the Cloudflare Worker.
const SPACE_ID = 'hn6mjgy0jkd9'
const PREVIEW_TOKEN = 'm8wYgIrhbneY0-BdLebltMSq168t9--qRDpYBmXzzvo'
const CDN_BASE = `https://preview.contentful.com/spaces/${SPACE_ID}`

// Cloudflare Worker handles writes securely (CMA token stored there, not in frontend)
const WRITE_PROXY_URL =
  'https://contentful-write-proxy.biz-belodigital.workers.dev/'

// ─── Auth (validate admin credentials via Cloudflare Worker) ───
export async function authenticateAdmin(
  username: string,
  password: string,
): Promise<boolean> {
  try {
    const res = await fetch(`${WRITE_PROXY_URL}auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    })
    if (!res.ok) return false
    const data = await res.json()
    return data.success === true
  } catch {
    return false
  }
}

// ─── Write (publish to CMS via Cloudflare Worker proxy) ───
export async function publishToContentful(
  data: Record<string, any>,
): Promise<void> {
  const res = await fetch(WRITE_PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || 'Failed to publish to CMS')
  }
}

// ─── Read (fetch from Contentful Preview API) ───
interface ContentfulResponse<T = any> {
  items: ContentfulEntry<T>[]
  includes?: {
    Asset?: ContentfulAsset[]
  }
  total?: number
}

interface ContentfulEntry<T = any> {
  sys: {
    id: string
    type: string
    createdAt: string
    updatedAt: string
    contentType?: {
      sys: { id: string }
    }
  }
  fields: T
}

interface ContentfulAsset {
  sys: { id: string }
  fields: {
    title?: string
    file?: {
      url: string
      details?: any
      fileName?: string
      contentType?: string
    }
  }
}

export async function fetchContentfulEntries<T = any>(
  contentType: string,
  params?: Record<string, string>,
): Promise<ContentfulEntry<T>[]> {
  const searchParams = new URLSearchParams({
    access_token: PREVIEW_TOKEN,
    content_type: contentType,
    ...params,
  })

  const url = `${CDN_BASE}/entries?${searchParams.toString()}`
  console.log(
    `[Contentful] Fetching ${contentType}:`,
    url.replace(PREVIEW_TOKEN, '***'),
  )

  const res = await fetch(url)

  if (!res.ok) {
    const errorText = await res.text()
    console.error(
      `[Contentful] Error fetching ${contentType}:`,
      res.status,
      errorText,
    )
    throw new Error(
      `Failed to fetch ${contentType}: ${res.status} - ${errorText}`,
    )
  }

  const data: ContentfulResponse<T> = await res.json()
  console.log(
    `[Contentful] ${contentType}: ${data.items?.length ?? 0} entries found (total: ${data.total})`,
  )

  if (data.items?.length > 0) {
    console.log(
      `[Contentful] ${contentType} sample fields:`,
      JSON.stringify(data.items[0].fields, null, 2),
    )
  }

  return data.items || []
}

// Fetch entries WITH linked assets (includes) — needed for resolving images
export async function fetchContentfulEntriesWithIncludes<T = any>(
  contentType: string,
  params?: Record<string, string>,
): Promise<{
  items: ContentfulEntry<T>[]
  includes?: { Asset?: ContentfulAsset[] }
}> {
  const searchParams = new URLSearchParams({
    access_token: PREVIEW_TOKEN,
    content_type: contentType,
    include: '1',
    ...params,
  })

  const url = `${CDN_BASE}/entries?${searchParams.toString()}`
  const res = await fetch(url)

  if (!res.ok) {
    const errorText = await res.text()
    throw new Error(
      `Failed to fetch ${contentType}: ${res.status} - ${errorText}`,
    )
  }

  const data: ContentfulResponse<T> = await res.json()
  console.log(
    `[Contentful] ${contentType} (with includes): ${data.items?.length ?? 0} entries, ${data.includes?.Asset?.length ?? 0} assets`,
  )

  return {
    items: data.items || [],
    includes: data.includes,
  }
}

// Helper to resolve an asset URL from Contentful's includes
export function resolveAssetUrl(
  assetLink: any,
  includes?: { Asset?: ContentfulAsset[] },
): string {
  if (!assetLink || !includes?.Asset) return ''
  const assetId = assetLink?.sys?.id
  if (!assetId) return ''
  const asset = includes.Asset.find((a) => a.sys.id === assetId)
  return asset?.fields?.file?.url ? `https:${asset.fields.file.url}` : ''
}

// ─── Diagnostic: list all content types in the space ───
export async function listContentTypes(): Promise<void> {
  try {
    const res = await fetch(
      `${CDN_BASE}/content_types?access_token=${PREVIEW_TOKEN}`,
    )
    if (!res.ok) {
      console.error('[Contentful] Failed to list content types:', res.status)
      return
    }
    const data = await res.json()
    console.log('[Contentful] ═══ Available Content Types ═══')
    data.items?.forEach((ct: any) => {
      const fieldNames = ct.fields?.map((f: any) => f.id).join(', ')
      console.log(`  → "${ct.sys.id}" (${ct.name}) — fields: [${fieldNames}]`)
    })
    console.log('[Contentful] ═══════════════════════════════')
  } catch (err) {
    console.error('[Contentful] Failed to list content types:', err)
  }
}

// Auto-run diagnostic on load
listContentTypes()
